import { useEffect, useMemo, useState } from "react";
import { monitoringReadApi } from "../api/monitoring";
import { agentManagementService, apiService, type Instance } from "../api/client";
import { useDataEnvironment } from "../contexts/DataEnvironmentContext";
import { useSurfaceAccess } from "../contexts/SurfaceAccessContext";
import { operationalSeverityRank } from "../utils/operationalStatus";
import {
  normalizeOperationalActionId,
  resolveOperationalMessageAction,
  type OperationalMessageActionRelatedEntities,
} from "../utils/operationalMessageActions";

export type OperationalMessageSummary = {
  id: number;
  target_type: string;
  target_id?: number | null;
  source_type?: string | null;
  source_id?: number | null;
  severity: string;
  state: string;
  title: string;
  body: string;
  recommended_action?: string | null;
  first_observed_at?: string | null;
  last_observed_at?: string | null;
  occurrence_count?: number | null;
  message_key?: string | null;
};

export type EnrichedOperationalMessage = OperationalMessageSummary & {
  targetLabel: string;
  targetName?: string;
  assignedDeviceName?: string;
  eventId?: number;
  eventDeviceId?: number;
  loadAssignmentId?: number;
  agentId?: number;
  navigationPath?: string;
};

type MonitorSummary = {
  id: number | string;
  inventory_device_id?: number | string | null;
  label?: string | null;
  key?: string | null;
};

type LoadAssignmentSummary = {
  id: number | string;
  monitor_id?: number | string | null;
  inventory_device_id?: number | string | null;
  virtual_device_id?: number | string | null;
  virtual_device_name?: string | null;
  load_name?: string | null;
  event_id?: number | string | null;
  status?: string | null;
};

type VirtualDeviceSummary = {
  id: number | string;
  instance_id?: number | string | null;
  zone_id?: number | string | null;
  name?: string | null;
};

type ZoneSummary = {
  id: number | string;
  event_id?: number | string | null;
};

type AgentSummary = {
  id: number | string;
  name?: string | null;
};

const normalizeDeviceId = normalizeOperationalActionId;

const formatTargetType = (targetType?: string | null) => {
  if (!targetType) return "Scope";
  return targetType
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const useOpenOperationalMessages = () => {
  const { dataEnvironment } = useDataEnvironment();
  const { canViewDeviceOperationalStatus, isSystemUser } = useSurfaceAccess();
  const [messages, setMessages] = useState<EnrichedOperationalMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadMessages = async () => {
      setLoading(true);
      setError(null);

      try {
        const rawMessages = (await monitoringReadApi.getOperationalMessages({
          state: "open",
          limit: 200,
        })) as OperationalMessageSummary[];

        if (rawMessages.length === 0) {
          if (!cancelled) {
            setMessages([]);
          }
          return;
        }

        const includesAgentMessages = rawMessages.some((message) => message.target_type === "agent" || message.source_type?.startsWith("agent"));

        const [devicesResponse, monitorsResponse, loadAssignmentsResponse, virtualDevicesResponse, zonesResponse, agentsResponse] = await Promise.all([
          apiService.getAll("instances", {}, "name", "asc"),
          apiService.getAll("monitors", {}, "display_order", "asc").catch(() => []),
          apiService.getAll("load_assignments", { status: "active" }, "id", "asc").catch(() => []),
          apiService.getAll("virtual_devices", {}, "name", "asc").catch(() => []),
          apiService.getAll("zones", {}, "name", "asc").catch(() => []),
          includesAgentMessages && isSystemUser ? agentManagementService.listAgents().catch(() => []) : Promise.resolve([]),
        ]);

        const devices = (devicesResponse || []) as Instance[];
        const monitors = (monitorsResponse || []) as MonitorSummary[];
        const loadAssignments = (loadAssignmentsResponse || []) as LoadAssignmentSummary[];
        const virtualDevices = (virtualDevicesResponse || []) as VirtualDeviceSummary[];
        const zones = (zonesResponse || []) as ZoneSummary[];
        const agents = (agentsResponse || []) as AgentSummary[];
        const devicesById = new Map<number, Instance>();
        const monitorsById = new Map<number, MonitorSummary>();
        const loadAssignmentsByMonitorId = new Map<number, LoadAssignmentSummary>();
        const virtualDeviceByInstanceId = new Map<number, VirtualDeviceSummary>();
        const eventIdByZoneId = new Map<number, number>();
        const agentsById = new Map<number, AgentSummary>();
        devices.forEach((device) => {
          const deviceId = normalizeDeviceId(device.id);
          if (deviceId !== null) {
            devicesById.set(deviceId, device);
          }
        });
        monitors.forEach((monitor) => {
          const monitorId = normalizeDeviceId(monitor.id);
          if (monitorId !== null) {
            monitorsById.set(monitorId, monitor);
          }
        });
        loadAssignments.forEach((assignment) => {
          const monitorId = normalizeDeviceId(assignment.monitor_id);
          if (monitorId !== null && !loadAssignmentsByMonitorId.has(monitorId)) {
            loadAssignmentsByMonitorId.set(monitorId, assignment);
          }
        });
        virtualDevices.forEach((virtualDevice) => {
          const instanceId = normalizeDeviceId(virtualDevice.instance_id);
          if (instanceId !== null && !virtualDeviceByInstanceId.has(instanceId)) {
            virtualDeviceByInstanceId.set(instanceId, virtualDevice);
          }
        });
        zones.forEach((zone) => {
          const zoneId = normalizeDeviceId(zone.id);
          const eventId = normalizeDeviceId(zone.event_id);
          if (zoneId !== null && eventId !== null) {
            eventIdByZoneId.set(zoneId, eventId);
          }
        });
        agents.forEach((agent) => {
          const agentId = normalizeDeviceId(agent.id);
          if (agentId !== null) {
            agentsById.set(agentId, agent);
          }
        });

        const navigationRelatedEntities: OperationalMessageActionRelatedEntities = {
          monitorsById,
          loadAssignmentsByMonitorId,
          virtualDeviceByInstanceId,
          eventIdByZoneId,
          agentsById,
        };

        const visibleMessages = rawMessages
          .filter((message) => {
            if (message.target_type === "device") {
              const deviceId = normalizeDeviceId(message.target_id);
              if (deviceId === null) return false;
              const device = devicesById.get(deviceId);
              if (!device) return isSystemUser;
              return canViewDeviceOperationalStatus(device.owner_group_id);
            }
            if (message.target_type === "agent") {
              return isSystemUser;
            }
            if (message.target_type !== "monitor") return false;
            const monitorId = normalizeDeviceId(message.target_id);
            if (monitorId === null) return false;
            const monitor = monitorsById.get(monitorId);
            const parentDeviceId = normalizeDeviceId(monitor?.inventory_device_id);
            if (parentDeviceId === null) return isSystemUser;
            const device = devicesById.get(parentDeviceId);
            if (!device) return isSystemUser;
            return canViewDeviceOperationalStatus(device.owner_group_id);
          })
          .map((message) => {
            const targetId = normalizeDeviceId(message.target_id);
            const targetTypeLabel = formatTargetType(message.target_type);
            if (message.target_type === "monitor") {
              const monitor = targetId === null ? undefined : monitorsById.get(targetId);
              const parentDeviceId = normalizeDeviceId(monitor?.inventory_device_id);
              const parentDevice = parentDeviceId === null ? undefined : devicesById.get(parentDeviceId);
              const assignment = targetId === null ? undefined : loadAssignmentsByMonitorId.get(targetId);
              const targetName = monitor?.label || (targetId === null ? undefined : `Monitor ${targetId}`);
              const assignedDeviceName = assignment?.load_name || targetName;
              const eventId = normalizeDeviceId(assignment?.event_id);
              const eventDeviceId = normalizeDeviceId(assignment?.virtual_device_id);
              const loadAssignmentId = normalizeDeviceId(assignment?.id);

              return {
                ...message,
                targetName,
                assignedDeviceName,
                eventId: eventId ?? undefined,
                eventDeviceId: eventDeviceId ?? undefined,
                loadAssignmentId: loadAssignmentId ?? undefined,
                targetLabel: parentDevice?.name
                  ? `${targetTypeLabel}: ${targetName} on ${parentDevice.name}`
                  : targetName
                    ? `${targetTypeLabel}: ${targetName}`
                    : targetTypeLabel,
                navigationPath: resolveOperationalMessageAction(message, navigationRelatedEntities)?.path,
              };
            }

            if (message.target_type === "agent") {
              const agentId = targetId ?? normalizeDeviceId(message.source_id);
              const agent = agentId === null ? undefined : agentsById.get(agentId);
              const targetName = agent?.name || (agentId === null ? undefined : `Agent ${agentId}`);

              return {
                ...message,
                targetName,
                assignedDeviceName: targetName,
                agentId: agentId ?? undefined,
                targetLabel: targetName ? `${targetTypeLabel}: ${targetName}` : targetTypeLabel,
                navigationPath: resolveOperationalMessageAction(message, navigationRelatedEntities)?.path,
              };
            }

            const deviceId = targetId;
            const device = deviceId === null ? undefined : devicesById.get(deviceId);
            const targetName = device?.name || (deviceId === null ? undefined : `Device ${deviceId}`);
            const eventDevice = deviceId === null ? undefined : virtualDeviceByInstanceId.get(deviceId);
            const eventDeviceId = normalizeDeviceId(eventDevice?.id);
            const eventId = normalizeDeviceId(eventDevice?.zone_id) === null
              ? null
              : eventIdByZoneId.get(normalizeDeviceId(eventDevice?.zone_id)!) ?? null;
            const assignedDeviceName =
              deviceId === null ? undefined : eventDevice?.name || targetName;

            return {
              ...message,
              targetName,
              assignedDeviceName,
              eventId: eventId ?? undefined,
              eventDeviceId: eventDeviceId ?? undefined,
              targetLabel: targetName ? `${targetTypeLabel}: ${targetName}` : targetTypeLabel,
              navigationPath: resolveOperationalMessageAction(message, navigationRelatedEntities)?.path,
            };
          })
          .sort((a, b) => {
            const severityDelta = operationalSeverityRank(b.severity) - operationalSeverityRank(a.severity);
            if (severityDelta !== 0) return severityDelta;
            return String(b.last_observed_at || "").localeCompare(String(a.last_observed_at || ""));
          });

        if (!cancelled) {
          setMessages(visibleMessages);
        }
      } catch (err) {
        console.error("Failed to load open operational messages:", err);
        if (!cancelled) {
          setMessages([]);
          setError("Open operational messages are unavailable.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const path = typeof window === "undefined" ? "" : window.location.pathname;
    const delayMs = path === "/messages" ? 0 : 1200;
    const timer = window.setTimeout(() => {
      void loadMessages();
    }, delayMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canViewDeviceOperationalStatus, dataEnvironment, isSystemUser]);

  const worstSeverity = useMemo(() => {
    return messages.reduce<string | null>((worst, message) => {
      if (operationalSeverityRank(message.severity) > operationalSeverityRank(worst)) {
        return message.severity;
      }
      return worst;
    }, null);
  }, [messages]);

  return {
    messages,
    loading,
    error,
    count: messages.length,
    worstSeverity,
  };
};

export default useOpenOperationalMessages;
